// arsenal-tpms/frontend/src/components/schematics/VehicleTelemetryModal/utils.js
// UTILIDADES COMPARTIDAS DEL MODAL DE TELEMETRÍA

import i18n from '../../../i18n/config';
import Highcharts from 'highcharts';

// Unit conversion utilities
export const unitConversionUtils = (unitPreferences) => ({
  convertPressure: (value, fromUnit = 'PSI', toUnit = null) => {
    if (!value || isNaN(value)) return null;

    const targetUnit = toUnit || unitPreferences?.pressure_unit || 'PSI';
    if (fromUnit === targetUnit) return value;

    // Convert to PSI first as base unit
    let psiValue = value;
    if (fromUnit === 'BAR') {
      psiValue = value * 14.5038;
    } else if (fromUnit === 'kPa') {
      psiValue = value * 0.145038;
    }

    // Convert from PSI to target unit
    if (targetUnit === 'BAR') {
      return psiValue / 14.5038;
    } else if (targetUnit === 'kPa') {
      return psiValue / 0.145038;
    }

    return psiValue;
  },

  convertTemperature: (value, fromUnit = 'C', toUnit = null) => {
    if (!value || isNaN(value)) return null;

    const targetUnit = toUnit || unitPreferences?.temperature_unit || 'C';
    if (fromUnit === targetUnit) return value;

    // Convert to Celsius first as base unit
    let celsiusValue = value;
    if (fromUnit === 'F') {
      celsiusValue = (value - 32) * 5 / 9;
    } else if (fromUnit === 'K') {
      celsiusValue = value - 273.15;
    }

    // Convert from Celsius to target unit
    if (targetUnit === 'F') {
      return celsiusValue * 9 / 5 + 32;
    } else if (targetUnit === 'K') {
      return celsiusValue + 273.15;
    }

    return celsiusValue;
  },

  convertAcceleration: (value, fromUnit = 'G', toUnit = null) => {
    if (!value || isNaN(value)) return null;

    const targetUnit = toUnit || unitPreferences?.acceleration_unit || 'G';
    if (fromUnit === targetUnit) return value;

    // Convert to G first as base unit
    let gValue = value;
    if (fromUnit === 'm/s2') {
      gValue = value / 9.80665;
    } else if (fromUnit === 'ft/s2') {
      gValue = value / 32.174;
    }

    // Convert from G to target unit
    if (targetUnit === 'm/s2') {
      return gValue * 9.80665;
    } else if (targetUnit === 'ft/s2') {
      return gValue * 32.174;
    }

    return gValue;
  }
});

// Get unit symbol
export const getUnitSymbol = (unit) => {
  const symbols = {
    // Pressure
    'PSI': 'psi',
    'BAR': 'bar',
    'kPa': 'kPa',
    'kg/cm2': 'kg/cm²',
    'atm': 'atm',
    'hPa': 'hPa',
    'mmHg': 'mmHg',
    'inHg': 'inHg',

    // Temperature
    'C': '°C',
    'F': '°F',
    'K': 'K',
    'R': '°R',

    // Acceleration
    'G': 'g',
    'm/s2': 'm/s²',
    'ft/s2': 'ft/s²',

    // Speed
    'km/h': 'km/h',
    'mph': 'mph',
    'knots': 'kn',
    'm/s': 'm/s'
  };

  return symbols[unit] || unit;
};

// Get metric unit
export const getMetricUnit = (metric, unitPreferences) => {
  if (!unitPreferences) {
    switch (metric) {
      case 'pressure': return 'PSI';
      case 'temperature': return '°C';
      case 'acceleration': return 'g';
      default: return '';
    }
  }

  switch (metric) {
    case 'pressure': return getUnitSymbol(unitPreferences.pressure_unit);
    case 'temperature': return getUnitSymbol(unitPreferences.temperature_unit);
    case 'acceleration': return getUnitSymbol(unitPreferences.acceleration_unit);
    default: return '';
  }
};

// Get metric display name
export const getMetricDisplayName = (metric) => {
  switch (metric) {
    case 'pressure': return i18n.t('hardcoded.components_schematics_vehicletelemetrymodal_utils.presion');
    case 'temperature': return i18n.t('hardcoded.components_schematics_vehicletelemetrymodal_utils.temperatura');
    case 'acceleration': return i18n.t('hardcoded.components_schematics_vehicletelemetrymodal_utils.aceleracion');
    default: return i18n.t('hardcoded.components_schematics_vehicletelemetrymodal_utils.metrica');
  }
};

// Format value with unit preferences
export const formatValue = (value, metric, unitPreferences) => {
  if (value === null || value === undefined || isNaN(value)) return '--';

  if (!unitPreferences) {
    switch (metric) {
      case 'pressure': return `${value.toFixed(1)} PSI`;
      case 'temperature': return `${value.toFixed(1)}°C`;
      case 'acceleration': return `${value.toFixed(2)}g`;
      default: return value.toFixed(2);
    }
  }

  const globalDecimals = parseInt(unitPreferences.decimal_places) || 2;
  const decimalMap = unitPreferences.decimal_places_map || unitPreferences.display_settings || {};
  const pressureDecimals = Number.isInteger(decimalMap?.pressure) ? decimalMap.pressure : Number.isInteger(decimalMap?.pressure?.decimals) ? decimalMap.pressure.decimals : globalDecimals;
  const temperatureDecimals = Number.isInteger(decimalMap?.temperature) ? decimalMap.temperature : Number.isInteger(decimalMap?.temperature?.decimals) ? decimalMap.temperature.decimals : 1;
  const accelerationDecimals = Number.isInteger(decimalMap?.acceleration) ? decimalMap.acceleration : Number.isInteger(decimalMap?.acceleration?.decimals) ? decimalMap.acceleration.decimals : globalDecimals;

  switch (metric) {
    case 'pressure':
      return `${value.toFixed(pressureDecimals)} ${getUnitSymbol(unitPreferences.pressure_unit)}`;
    case 'temperature':
      return `${value.toFixed(temperatureDecimals)}${getUnitSymbol(unitPreferences.temperature_unit)}`;
    case 'acceleration':
      return `${value.toFixed(accelerationDecimals)}${getUnitSymbol(unitPreferences.acceleration_unit)}`;
    default:
      return value.toFixed(globalDecimals);
  }
};

// Format date for input (dd/mmm/aaaa)
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['ene', 'feb', 'mar', 'abr', 'may', 'jun',
      'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    return dateString;
  }
};

// Format date for chart labels
export const formatDateLocal = (timestamp) => {
  try {
    return Highcharts.dateFormat('%e/%b/%Y', timestamp);
  } catch (error) {
    console.error('❌ Error formatting date local:', error);
    return new Date(timestamp).toLocaleDateString();
  }
};

// Calculate date range
export const calculateDateRange = (rangeType, timeRangeConfig) => {
  const config = timeRangeConfig[rangeType];
  const now = new Date();
  const endDate = new Date(now);
  const startDate = new Date(now);

  if (rangeType === '1hour') {
    startDate.setHours(now.getHours() - 1);
  } else {
    startDate.setDate(now.getDate() - config.duration);
  }

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    config
  };
};

// Create schematic layout
export const createSchematicLayout = (vehicle) => {
  if (!vehicle?.positions_count) return [];

  if (!vehicle.figure) {
    return [Array.from({ length: vehicle.positions_count }, (_, i) => i + 1)];
  }

  const axleDistribution = vehicle.figure.split(',').map(a => parseInt(a, 10)).filter(num => !isNaN(num));
  const figureTotal = axleDistribution.reduce((sum, positions) => sum + positions, 0);

  let layout = [];
  let positionCounter = 1;

  if (figureTotal === vehicle.positions_count) {
    axleDistribution.forEach(positionsPerAxle => {
      const row = [];
      for (let i = 0; i < positionsPerAxle; i++) {
        row.push(positionCounter++);
      }
      layout.push(row);
    });
  } else {
    const scaleFactor = vehicle.positions_count / figureTotal;
    let remainingPositions = vehicle.positions_count;

    for (let axleIndex = 0; axleIndex < axleDistribution.length; axleIndex++) {
      if (axleIndex === axleDistribution.length - 1) {
        const row = [];
        for (let i = 0; i < remainingPositions; i++) {
          row.push(positionCounter++);
        }
        if (row.length > 0) layout.push(row);
        break;
      }

      const originalPositions = axleDistribution[axleIndex];
      const scaledPositions = Math.round(originalPositions * scaleFactor);
      const positionsToUse = Math.min(scaledPositions, remainingPositions);

      const row = [];
      for (let i = 0; i < positionsToUse; i++) {
        row.push(positionCounter++);
      }

      if (row.length > 0) {
        layout.push(row);
        remainingPositions -= row.length;
      }

      if (remainingPositions <= 0) break;
    }
  }

  return layout;
};

// Get available positions
export const getAvailablePositions = (vehicle) => {
  if (!vehicle?.vehicle_sensors) return [];
  return vehicle.vehicle_sensors.map(vs => vs.position).sort((a, b) => a - b);
};

// Convert telemetry value
export const convertTelemetryValue = (rawData, metricType, unitPreferences, conversionUtils) => {
  if (!rawData || !unitPreferences) return null;

  const parseNumericValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const cleanValue = value.replace(',', '.');
      const parsed = parseFloat(cleanValue);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  switch (metricType) {
    case 'pressure':
      const rpsi = parseNumericValue(rawData.rpsi);
      if (rpsi !== null && rpsi >= 0) {
        const kPa = 2.75 * rpsi + 20;
        const psi_base = kPa * 0.145038;
        const convertedValue = conversionUtils.convertPressure(psi_base, 'PSI');
        return convertedValue;
      }
      return null;

    case 'temperature':
      const rtemp = parseNumericValue(rawData.rtemp);
      if (rtemp !== null && rtemp !== 0) {
        const celsius_base = 1 * rtemp - 55;
        const convertedValue = conversionUtils.convertTemperature(celsius_base, 'C');
        return convertedValue;
      }
      return null;

    case 'acceleration':
      const peak = parseNumericValue(rawData.peak);
      if (peak !== null) {
        const convertedValue = conversionUtils.convertAcceleration(peak, 'G');
        return convertedValue;
      }
      return null;

    default:
      return null;
  }
};

// Get X-axis format based on time range
export const getXAxisFormat = (selectedTimeRange, formatters = {}) => {
  const isLegacyFormatter = typeof formatters === 'function';

  const formatDateLocal = isLegacyFormatter
    ? formatters
    : formatters.formatDateLocal;

  const formatTimeLocal = isLegacyFormatter
    ? null
    : formatters.formatTimeLocal;

  const formatDayMonthLocal = isLegacyFormatter
    ? null
    : formatters.formatDayMonthLocal;

  const safeFormatTime = (value) => {
    if (typeof formatTimeLocal === 'function') {
      return formatTimeLocal(value);
    }
    return Highcharts.dateFormat('%H:%M', value);
  };

  const safeFormatDayMonth = (value) => {
    if (typeof formatDayMonthLocal === 'function') {
      return formatDayMonthLocal(value);
    }
    return Highcharts.dateFormat('%d/%m', value);
  };

  const safeFormatDate = (value) => {
    if (typeof formatDateLocal === 'function') {
      return formatDateLocal(value);
    }
    return Highcharts.dateFormat('%e/%b/%Y', value);
  };

  if (selectedTimeRange === '1hour') {
    return {
      dateTimeLabelFormats: {
        minute: '%H:%M',
        hour: '%H:%M',
        day: '%d/%m %H:%M'
      },
      formatter: function () {
        return safeFormatTime(this.value);
      }
    };
  } else if (selectedTimeRange === '1day') {
    return {
      dateTimeLabelFormats: {
        hour: '%H:%M',
        day: '%d/%m %H:%M',
        week: '%d/%m'
      },
      formatter: function () {
        return safeFormatTime(this.value);
      }
    };
  } else if (selectedTimeRange === '1week') {
    return {
      dateTimeLabelFormats: {
        day: '%d/%m',
        week: '%d/%m',
        month: '%d/%m'
      },
      formatter: function () {
        return safeFormatDayMonth(this.value);
      }
    };
  } else {
    return {
      dateTimeLabelFormats: {
        day: '%d/%m',
        week: '%d/%m',
        month: '%b %Y',
        year: '%Y'
      },
      formatter: function () {
        return safeFormatDate(this.value);
      }
    };
  }
};

export default {
  unitConversionUtils,
  getUnitSymbol,
  getMetricUnit,
  getMetricDisplayName,
  formatValue,
  formatDateForInput,
  formatDateLocal,
  calculateDateRange,
  createSchematicLayout,
  getAvailablePositions,
  convertTelemetryValue,
  getXAxisFormat
};